import React, { useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { closeModal, selectIsModalOpen, GLOBAL_MODAL_TYPE } from 'state/constants'
import {
    selectSubmissionPayoutProgress,
    selectSubmissionMassPayoutSuccess,
    selectSubmissionMassPayoutFailed,
} from 'state'
import { Box, Flex, Text } from 'rebass'
import { useNewTranslation } from 'hooks'
import { theme } from 'theme'
import { massPaySubmission } from 'state/submissions'
import ConfirmPopUp from 'components/Shared/ConfirmPopUp'
import ListApprovalPopUp from 'components/Shared/ListApprovePopUp'
import Progress from 'components/Shared/Progress'
import MassImportResult from 'components/Shared/MassImportResult'
import ModalStyledPopup from 'components/ModalStyledPopup'
import styled from 'particles/themes/styled'
import Icon from 'components/Icon/Icon'
import { MassPayoutData } from './Payouts'
import { PLATFORM_ENUM } from 'services/api/constants'

enum ActionStep {
    ClOSE,
    IMPORT,
    CONFIRM,
    PROCESS,
    RESULT,
}
const PopupContainer = styled(Box)`
    overflow: hidden;
`
interface ListApprovePayoutPopUpProps {
    campaignUuid: string
    massApprovePayoutInfo: MassPayoutData[]
}
export interface MassList {
    [name: string]: null | string
}
const ListApprovePayoutPopUp: React.FC<ListApprovePayoutPopUpProps> = ({ campaignUuid, massApprovePayoutInfo }) => {
    const [step, setStep] = useState(ActionStep.IMPORT)
    const approvalPopupData = useRef<{ usernames: string[]; platform: PLATFORM_ENUM }>({
        usernames: [],
        platform: PLATFORM_ENUM.IG,
    })
    const [remainSlotsInfo, setRemainSlotsInfo] = useState({ remainSlotsError: false, listUserTotalNum: 0 })
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const isApproveModalOpen = useSelector(selectIsModalOpen(GLOBAL_MODAL_TYPE.MASS_APPROVAL))
    const { count, total } = useSelector(selectSubmissionPayoutProgress(campaignUuid))
    const successRows = useSelector(selectSubmissionMassPayoutSuccess(campaignUuid))
    const failedRows = useSelector(selectSubmissionMassPayoutFailed(campaignUuid))
    const onCloseModal = useCallback(() => dispatch(closeModal()), [])
    const onSubmitIncreaseSlots = () => {
        if (approvalPopupData.current.usernames.length === 0) {
            return
        }

        const { usernames, platform } = approvalPopupData.current
        const massApproveList: MassList = {}
        usernames.forEach(username => {
            const submissionId = massApprovePayoutInfo.find(item => item.name === username)
            if (submissionId) {
                massApproveList[username] = submissionId.submissionId
            } else {
                massApproveList[username] = null
            }
        })
        dispatch(
            massPaySubmission({
                campaignUuid: campaignUuid,
                platform: platform,
                usernames: massApproveList,
                moveNextStep: () => {
                    setStep(ActionStep.RESULT)
                },
            }),
        )
        setStep(ActionStep.PROCESS)
    }
    const onOpenConfirm = useCallback((data: { usernames: string[]; platform: PLATFORM_ENUM }) => {
        approvalPopupData.current = data
        setStep(ActionStep.CONFIRM)
    }, [])
    const recall = () => {
        setStep(ActionStep.IMPORT)
        onCloseModal()
    }
    const resetRemainSlotsInfo = (totalUserNum: number, isRemainSlosError: boolean) => {
        setRemainSlotsInfo({ ...remainSlotsInfo, remainSlotsError: isRemainSlosError, listUserTotalNum: totalUserNum })
    }

    return (
        <>
            {step !== ActionStep.CONFIRM && isApproveModalOpen && (
                <ModalStyledPopup open={isApproveModalOpen} width="773px" closeOnDocumentClick={false}>
                    <PopupContainer m={theme.spacing.s} height={'100%'}>
                        <Flex justifyContent="center" paddingBottom={theme.spacing.xxxs}>
                            <Icon.TermsAndCondition fill={theme.colors.governorBay} />
                        </Flex>
                        <Text
                            color={theme.colors.governorBay}
                            fontSize="24px"
                            textAlign="center"
                            mb={theme.spacing.xxxxs}
                        >
                            {t('submission.importList.payoutListPageTitle')}
                        </Text>
                        {step === ActionStep.IMPORT && (
                            <ListApprovalPopUp
                                close={onCloseModal}
                                onClickNextMove={onOpenConfirm}
                                setTotal={resetRemainSlotsInfo}
                            >
                                <Box my={theme.spacing.xxs}>
                                    <Box color={theme.colors.mineShaft} mx={theme.spacing.xxxs} mb={theme.spacing.xs}>
                                        <Text fontSize="1.2em">{t('submission.importList.stepsHeader')}</Text>
                                        <Text style={{ lineHeight: '28px' }}>
                                            <ul style={{ paddingInlineStart: '20px', listStyle: 'none' }}>
                                                <li>{t('submission.importList.stepOne')}</li>
                                                <li>{t('submission.importList.stepTwo')}</li>
                                                <li>{t('submission.importList.stepThree')}</li>
                                            </ul>
                                        </Text>
                                        <Box textAlign="center" my={theme.spacing.xxxs}>
                                            <Text fontSize="1.3em">
                                                {t('submission.importList.numberOfPartiposterSelectedHeader', {
                                                    count:
                                                        remainSlotsInfo.listUserTotalNum > 200
                                                            ? 0
                                                            : remainSlotsInfo.listUserTotalNum,
                                                })}
                                            </Text>
                                            <Text fontSize="0.8em">
                                                {t('submission.importList.availableSlotsPayoutSubtext')}
                                            </Text>
                                        </Box>
                                    </Box>
                                </Box>
                            </ListApprovalPopUp>
                        )}
                        {step === ActionStep.PROCESS && <Progress total={total} count={count} />}
                        {step === ActionStep.RESULT && (
                            <MassImportResult
                                close={recall}
                                actionType={3}
                                successRows={successRows}
                                failedRows={failedRows}
                                isPayout
                            />
                        )}
                    </PopupContainer>
                </ModalStyledPopup>
            )}

            <ConfirmPopUp show={step === 2} setShow={recall} onConfirm={onSubmitIncreaseSlots}>
                <Text color={theme.colors.governorBay} fontSize={'20px'} textAlign={'center'} mb={theme.spacing.xxxxs}>
                    {t('modalWindow.confirmationMessage.continueTitle')}
                </Text>

                <Flex flexDirection="column" justifyContent="center">
                    <Text fontSize={'14px'} display="inline" textAlign="center">
                        {t('modalWindow.continueProcessPayoutSubtitle')}
                    </Text>
                </Flex>
            </ConfirmPopUp>
        </>
    )
}

export default ListApprovePayoutPopUp
